import { Box, HStack, Select, Stack } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { Alert, AutoComplete } from "antd";
import { useIdentity } from "context/auth";
import useFeatureFlags from "hooks/useFeatureFlags";
import { useGetPayoutFees } from "hooks/usePayoutFees";
import useProfile from "hooks/useProfile";
import { useAuthenticatedRequest } from "hooks/useRequests";
import { FC, Suspense } from "react";
import FormInput from "./Inputs/FormInput";

const usePayPalHistory = () => {
    const query = useAuthenticatedRequest<{ emails: string[] }>({
        method: "GET",
        url: "/payout-requests/paypal/history"
    });

    return useQuery(["payout-requests", "paypal", "history"], query);
}

const PaypalFeesDisclosure: FC = () => {
    const { data: allFees } = useGetPayoutFees();
    const paypalPercentFee = allFees?.paypalDisplay ?? 0;

    return (
        <HStack spacing={1} color="gray">
            <Box fontWeight="bold">Note: </Box>
            <div>Paypal takes a</div>
            <Box as="span" fontWeight="bold">
                {paypalPercentFee}%
            </Box>
            <div>fee out of all payouts</div>
        </HStack>
    );
};

const PaypalPayoutDetails: FC = () => {
    const { isAdmin } = useIdentity();
    const { data: profile, isLoading: isLoadingProfile } = useProfile();
    const { shouldConfirmPayoutRequests } = useFeatureFlags();
    const { data, isLoading } = usePayPalHistory();
    const emails = data?.emails ?? [];
    const { requireTwoFactor = false } = profile ?? {};

    const confirmationEnabled = shouldConfirmPayoutRequests();
    const allowNewAddress = isAdmin || requireTwoFactor || confirmationEnabled;

    return (<>
        <Stack direction={{ base: "column", md: "row" }}>
            {(allowNewAddress) && <FormInput
                name="email"
                label="PayPal Email"
                required
            >
                <AutoComplete
                    disabled={isLoading || isLoadingProfile}
                    options={emails.map(e => ({ value: e }))}
                    placeholder="Select a PayPal Email"
                    filterOption={(input, option) =>
                        option!.value.toLocaleUpperCase().indexOf(input.toLocaleUpperCase()) !== -1
                    }
                    style={{ minWidth: '15rem' }}
                    popupClassName="autocomplete-fix"
                    defaultValue={null}
                />
            </FormInput>}

            {!allowNewAddress && <FormInput
                name="email"
                label="PayPal Email"
                required
            >
                <Select
                    disabled={isLoading || isLoadingProfile}
                    minWidth="15rem"
                    size="sm"
                    placeholder="Select a PayPal Email"
                    borderColor="#d9d9d9"
                    borderRadius={2}
                    outline="none"
                    defaultValue={undefined}
                >
                    {emails.map(e => (<option value={e}>{e}</option>))}
                </Select>
            </FormInput>}
            <Suspense>
                <PaypalFeesDisclosure />
            </Suspense>
        </Stack>
        {!allowNewAddress && <Alert type="warning"
            showIcon
            description="PayPal requests can only be sent to previously known email addresses. Please contact us if you need a payout to go to a different email address, or enable MFA in your profile page to lift this restriction."
        />}
    </>);
};

export default PaypalPayoutDetails;
